import { SPOTIFY_API_BASE } from "./constants";
import type { SpotifyPlaylistsResponse, SpotifyUserProfile } from "./types";

async function fetchWithToken(
  url: string,
  token: string,
  options?: RequestInit,
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Spotify API error: ${response.status} ${response.statusText}`);
  }

  return response;
}

export async function getUserPlaylists(
  token: string,
  limit = 50,
  offset = 0,
): Promise<SpotifyPlaylistsResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });
  const res = await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/playlists?${params}`,
    token,
  );
  return res.json();
}

export async function getCurrentUserProfile(
  token: string,
): Promise<SpotifyUserProfile> {
  const res = await fetchWithToken(`${SPOTIFY_API_BASE}/me`, token);
  return res.json();
}

export async function startPlayback(
  token: string,
  deviceId: string,
  contextUri: string,
): Promise<void> {
  await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/player/play?device_id=${deviceId}`,
    token,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ context_uri: contextUri }),
    },
  );
}

export async function pausePlayback(
  token: string,
  deviceId: string,
): Promise<void> {
  await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/player/pause?device_id=${deviceId}`,
    token,
    { method: "PUT" },
  );
}

export async function skipToNext(
  token: string,
  deviceId: string,
): Promise<void> {
  await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/player/next?device_id=${deviceId}`,
    token,
    { method: "POST" },
  );
}

export async function skipToPrevious(
  token: string,
  deviceId: string,
): Promise<void> {
  await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/player/previous?device_id=${deviceId}`,
    token,
    { method: "POST" },
  );
}

export async function setVolume(
  token: string,
  deviceId: string,
  volumePercent: number,
): Promise<void> {
  await fetchWithToken(
    `${SPOTIFY_API_BASE}/me/player/volume?volume_percent=${Math.round(volumePercent)}&device_id=${deviceId}`,
    token,
    { method: "PUT" },
  );
}
